import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { dontUpsell } from '@trello/browser';

import type { Action } from './BoardWouldPushWorkspaceOverLimit';

export interface ContentProps {
  action: Action;
  isFreeTrialEligible: boolean;
}

export const Content: FunctionComponent<ContentProps> = ({
  action,
  isFreeTrialEligible,
}) => {
  const overLimitDefaultMessage = (
    <FormattedMessage
      id="templates.workspace_user_limit.workspace-is-over-limit"
      defaultMessage="This free Workspace is over the 10 collaborator limit."
      description="message shown on an upgrade prompt when a free workspace exceeds the collaborator limit"
    />
  );

  switch (action) {
    case 'reopen': {
      if (dontUpsell()) {
        return (
          <FormattedMessage
            id="templates.board_closed.not-enough-space-no-upsell"
            defaultMessage="This free Workspace doesn't have enough space for more collaborators. To reopen this board in this Workspace, remove collaborators."
            description="This free Workspace doesn't have enough space for more collaborators. To reopen this board in this Workspace, remove collaborators."
          />
        );
      }

      return (
        <FormattedMessage
          id="templates.board_closed.not-enough-space"
          defaultMessage="This free Workspace doesn't have enough space for more collaborators. To reopen this board in this Workspace, upgrade now or remove collaborators."
          description="This free Workspace doesn't have enough space for more collaborators. To reopen this board in this Workspace, upgrade now or remove collaborators."
        />
      );
    }
    case 'move': {
      if (isFreeTrialEligible) {
        return (
          <>
            {overLimitDefaultMessage}{' '}
            <FormattedMessage
              id="templates.workspace_user_limit.start-a-trial-to-move-board"
              defaultMessage="To move boards into this Workspace, start a free trial or remove collaborators."
              description="message shown on an upgrade prompt when a free workspace exceeds the collaborator limit"
            />
          </>
        );
      }

      if (dontUpsell()) {
        return overLimitDefaultMessage;
      }

      return (
        <>
          {overLimitDefaultMessage}{' '}
          <FormattedMessage
            id="templates.workspace_user_limit.upgrade-to-move-board"
            defaultMessage="To move boards into this Workspace, upgrade the Workspace or remove collaborators."
            description="message shown on an upgrade prompt when a free workspace exceeds the collaborator limit"
          />
        </>
      );
    }
    case 'copy': {
      if (isFreeTrialEligible) {
        return (
          <>
            {overLimitDefaultMessage}{' '}
            <FormattedMessage
              id="templates.workspace_user_limit.start-a-trial-to-copy-board"
              defaultMessage="To copy boards into this Workspace, start a free trial or remove collaborators."
              description="message shown on an upgrade prompt when a free workspace exceeds the collaborator limit"
            />
          </>
        );
      }

      if (dontUpsell()) {
        return overLimitDefaultMessage;
      }

      return (
        <>
          {overLimitDefaultMessage}{' '}
          <FormattedMessage
            id="templates.workspace_user_limit.upgrade-to-copy-board"
            defaultMessage="To copy boards into this Workspace, upgrade the Workspace or remove collaborators."
            description="message shown on an upgrade prompt when a free workspace exceeds the collaborator limit"
          />
        </>
      );
    }
    case 'share': {
      if (dontUpsell()) {
        return overLimitDefaultMessage;
      }

      return (
        <FormattedMessage
          id="templates.workspace_user_limit.workspace-is-over-limit-to-share"
          defaultMessage="This free Workspace has reached the 10 collaborator limit. To share boards with new collaborators, upgrade or remove Workspace members, guests, or pending invitations."
          description="message shown on in the member search autocomplete when a free workspace exceeds the collaborator limit when trying to share"
        />
      );
    }
    default:
      throw new Error(
        `Unknown action "${action}" for BoardWouldPushWorkspaceOverLimit`,
      );
  }
};
